import { useCallback, useEffect, useState } from "react";

import { SharedItemClient } from "@/Common/Api/SharedItem/SharedItemClient";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { PatientBannerVertical } from "@/Common/Components/Patient/PatientBannerVertical";
import { usePortalCommunicationMessageForm } from "@/Common/Components/SharedItem/Hooks/usePortalCommunicationMessageForm";
import { PortalCommunicationMessageForm } from "@/Common/Components/SharedItem/PortalCommunicationMessageForm";
import { PortalCommunicationMessagesList } from "@/Common/Components/SharedItem/PortalCommunicationMessagesList";
import { PortalCommunicationRevokeAllPopup } from "@/Common/Components/SharedItem/PortalCommunicationRevokeAllPopup";
import { PortalCommunicationsMessagesNote } from "@/Common/Components/SharedItem/PortalCommunicationsMessagesNote";
import { displayToast, MessageToast } from "@/Common/Components/Toast/Toast";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { PatientBannerHelper } from "@/Common/Helpers/PatientBannerHelper";
import { PortalHelper } from "@/Common/Helpers/PortalHelper";
import { Patient } from "@/Common/Models/Patient/Patient";
import { IPatientBannerModel } from "@/Common/Models/Patient/UI/IPatientBannerModel";
import { Person } from "@/Common/Models/Person/Person";
import { PortalSyncStatus } from "@/Common/Models/Portal/Enums/PortalSyncStatus";
import { GetPatientOverviewResponse } from "@/Common/Models/Portal/MedfarPortal";
import { LinkedItem } from "@/Common/Models/Portal/SharedItem/LinkedItem";
import { SharedItem } from "@/Common/Models/Portal/SharedItem/SharedItem";
import { SharedItemActionType } from "@/Common/Models/Portal/SharedItem/SharedItemActionType";
import { SharedItemMessage } from "@/Common/Models/Portal/SharedItem/SharedItemMessage";
import { SharedItemPriority } from "@/Common/Models/Portal/SharedItem/SharedItemPriority";
import { Button } from "@/DesignSystem/Molecules/Button/Button";
import { IPaginationViewControlProps } from "../Pagination/PaginationViewControl";

import styles from "./PortalCommunicationsView.module.scss";

interface IPortalCommunicationsViewProps {
    currentSharedItemId?: string;
    patient?: Patient;
    showPatientBanner?: boolean;
    pagination?: React.ReactElement<IPaginationViewControlProps>;
    showFlagForReview?: boolean;
    showCloseButton?: boolean;
    usedDynamicHeight?: boolean;
    onArchivedRestore?: (sharedItemId: string) => void;
    onCloseCommunication?: () => void;
    onPortalCommunicationsCountRefresh?: () => void;
    onRevokeAll?: (sharedItemId: string, status: PortalSyncStatus, includeLinkedItem: boolean, date: Date) => void;
    onToggleHistoryReadStatus?: (sharedItemId: string, isMarkAsRead: boolean) => void;
    onMessageSent?: (sharedItemId: string, messages: SharedItemMessage[]) => void;
    onSyncOrRevokeSharedItemsAttachment?: (sharedItemId: string, status: PortalSyncStatus, includeLinkedItem: boolean, date: Date) => void;
}

function PortalCommunicationsView(props: Readonly<IPortalCommunicationsViewProps>): JSX.Element {
    const [currentSharedItem, setCurrentSharedItem] = useState<SharedItem>();
    const [linkedItem, setLinkedItem] = useState<LinkedItem>();
    const [actionType, setActionType] = useState<SharedItemActionType>(SharedItemActionType.View);
    const [portalAccountOverview, setPortalAccountOverview] = useState<GetPatientOverviewResponse>(null);
    const [isSendInProgress, setIsSendInProgress] = useState(false);
    const [showRevokeAllWarningPopup, setShowRevokeAllWarningPopup] = useState(false);
    const [patient, setPatient] = useState<Patient>(props.patient);
    const [patientBannerModel, setPatientBannerModel] = useState<IPatientBannerModel>();
    const [isArchiveCheckboxChecked, setIsArchiveCheckboxChecked] = useState(false);
    const [lastSharedItemIdWhenCountersWereRefreshed, setLastSharedItemIdWhenCountersWereRefreshed] = useState("");
    const { onPortalCommunicationsCountRefresh } = props;

    const { getText } = useTranslation();

    const {
        formState,
        isFormValid,
        onSetFormState
    } = usePortalCommunicationMessageForm();

    useEffect(() => {
        if (currentSharedItem) {
            onSetFormState({ canPatientReplyWithMessage: currentSharedItem.CanPatientReplyWithMessage });
        }
    }, [currentSharedItem, onSetFormState]);

    const loadPatientData = useCallback(async (patientContactId: string): Promise<void> => {
        const patient = await SharedItemClient.getPatientData(patientContactId);
        if (!patient)
            return;
        setPatient(patient);

        if (props.showPatientBanner) {
            await PatientBannerHelper.loadRamqInfo(patient);
            const patientBannerData = PatientBannerHelper.defaultPatientBannerModel().set("Model", patient);
            setPatientBannerModel(patientBannerData);
        }
    }, [props.showPatientBanner]);

    const setThread = useCallback(async (sharedItemId: string): Promise<void> => {
        if (!sharedItemId || sharedItemId === currentSharedItem?.SharedItemId)
            return;

        const sharedItemResponse = await SharedItemClient.getSharedItem(sharedItemId);
        if (!sharedItemResponse)
            return;

        setCurrentSharedItem(sharedItemResponse.SharedItem);
        setLinkedItem(sharedItemResponse.LinkedItem);

        if (props.patient) {
            setPatient(props.patient);
        }
        else {
            await loadPatientData(sharedItemResponse.SharedItem.PatientContactId);
            setIsArchiveCheckboxChecked(false);
        }
    }, [currentSharedItem?.SharedItemId, loadPatientData, props.patient]);

    const handlePatientMessagePriorityChange = async (sharedItemMessageId: number, priority: SharedItemPriority): Promise<void> => {
        if (currentSharedItem.Messages.some(message => message.SharedItemMessageId === sharedItemMessageId)) {
            const updatedSharedItemMessages = currentSharedItem.Messages.map(message => message.SharedItemMessageId === sharedItemMessageId ? { ...message, Priority: priority } : message);
            const response = await SharedItemClient.updatePortalCommunicationPriority(currentSharedItem.SharedItemId, sharedItemMessageId, priority);

            if (response) {
                setCurrentSharedItem({ ...currentSharedItem, Messages: updatedSharedItemMessages });
            }
        }
    };

    useEffect(() => {
        if (!props.currentSharedItemId) return;
        setThread(props.currentSharedItemId);
    }, [props.currentSharedItemId, setThread]);

    const handleSharedItemCategoryChange = async (categoryId: string): Promise<void> => {
        const response = await SharedItemClient.updatePortalCommunicationCategory(currentSharedItem.SharedItemId, categoryId);

        if (response) {
            setCurrentSharedItem({ ...currentSharedItem, SharedItemCategoryId: categoryId });
        }
    };

    const handleSharedItemMessageStatusToggleAsync = async (sharedItemMessageId: number, status: PortalSyncStatus.Revoked | PortalSyncStatus.Pending): Promise<void> => {
        // only get the messages that we want to toggle the status of
        const sharedItemMessagesToSync = currentSharedItem.Messages.filter(
            p => p.SharedItemMessageId === sharedItemMessageId
            && (
                (status === PortalSyncStatus.Revoked && PortalHelper.isStatusVisible(p.Status))
                || (status === PortalSyncStatus.Pending && !PortalHelper.isStatusVisible(p.Status))
            )
        );

        // only include the linked item if the attachment is currently visible
        const includeLinkedItem = PortalHelper.getSharedItemAttachmentIsVisible(currentSharedItem);

        const response = await SharedItemClient.syncUpsertSharedItemMessage(
            sharedItemMessagesToSync,
            currentSharedItem.PatientContactId,
            currentSharedItem.SharedItemCategoryId,
            currentSharedItem.SharedItemType,
            currentSharedItem.SharedItemId,
            null,
            currentSharedItem.CanPatientReplyWithMessage,
            linkedItem?.LinkedItemId,
            includeLinkedItem
        );

        if (!response) {
            return;
        }

        setCurrentSharedItem(sharedItem => new SharedItem({ ...sharedItem, Messages: response.Messages }));
    };

    const handleMarkPortalCommunicationAsUnread = async (): Promise<void> => {
        const response = await SharedItemClient.MarkPortalCommunicationAsUnread([currentSharedItem.SharedItemId]);
        if (response) {
            if (props.onCloseCommunication) {
                props.onCloseCommunication();
                return;
            }

            props.onToggleHistoryReadStatus?.(currentSharedItem.SharedItemId, false);
        }
    };

    const handleSendMessageClick = async (): Promise<void> => {
        setIsSendInProgress(true);

        const { messageText, priority, canPatientReplyWithMessage } = formState;
        const newMessage = SharedItemMessage.createNewSharedItemMessage(messageText, priority);

        try {
            // only include the linked item if the attachment is currently visible
            const includeLinkedItem = PortalHelper.getSharedItemAttachmentIsVisible(currentSharedItem);

            const response = await SharedItemClient.syncUpsertSharedItemMessage(
                [newMessage],
                patient.IndividualResponse.Contact_id,
                currentSharedItem.SharedItemCategoryId,
                currentSharedItem.SharedItemType,
                currentSharedItem.SharedItemId,
                null,
                canPatientReplyWithMessage,
                linkedItem?.LinkedItemId,
                includeLinkedItem
            );

            if (response) {
                const newThread = new SharedItem({
                    ...response.SharedItem,
                    Notes: currentSharedItem.Notes ?? [],
                    Files: currentSharedItem.Files ?? [],
                    Messages: response.Messages.slice() // copy the array since we're filtering and sorting below
                });
                setCurrentSharedItem(newThread);

                setActionType(SharedItemActionType.View);
                displayToast(MessageToast.Success, getText("SharedItem_MessageSentLabel"));

                props.onMessageSent?.(response.SharedItem.SharedItemId, response.Messages);
            }
        }
        finally {
            setIsSendInProgress(false);
        }
    };

    const revokeSharedItem = async (): Promise<void> => {
        try {
            await SharedItemClient.revokeSharedItemAsync(currentSharedItem.SharedItemId);
            setShowRevokeAllWarningPopup(false);

            if (props.onCloseCommunication) {
                props.onCloseCommunication();
                return;
            }
        }
        finally {
            setCurrentSharedItem(null);
            props.onRevokeAll?.(currentSharedItem.SharedItemId, PortalSyncStatus.Revoked, false, new Date());
        }
    };

    const syncOrRevokeSharedItemsAttachment = async (status: PortalSyncStatus.Revoked | PortalSyncStatus.Pending): Promise<void> => {
        const includeLinkedItem = status === PortalSyncStatus.Pending;

        const response = await SharedItemClient.syncOrRevokeSharedItemsAttachment(
            currentSharedItem.SharedItemId,
            currentSharedItem.SharedItemType,
            includeLinkedItem,
            linkedItem?.LinkedItemId
        );

        if (!response) {
            return;
        }

        setCurrentSharedItem(
            sharedItem => new SharedItem({
                ...sharedItem,
                Status: response.SharedItem.Status,
                IncludeLinkedItem: response.SharedItem.IncludeLinkedItem,
                HasPreviouslyIncludedItem: sharedItem.HasPreviouslyIncludedItem || sharedItem.IncludeLinkedItem,
                LastStatusDate: response.SharedItem.LastStatusDate
            })
        );

        props.onSyncOrRevokeSharedItemsAttachment?.(
            currentSharedItem.SharedItemId,
            response.SharedItem.Status,
            response.SharedItem.IncludeLinkedItem,
            response.SharedItem.LastStatusDate
        );
    };

    const handleFlagPortalCommunicationForReview = async (flagForReview: boolean): Promise<void> => {
        const response = await SharedItemClient.updateFlagPortalCommunicationForReview(currentSharedItem.SharedItemId, flagForReview);

        if (response) {
            setCurrentSharedItem({ ...currentSharedItem, IsFlaggedForReview: flagForReview });
        }
    };

    useEffect(() => {
        const loadPortalAccountOverview = async (): Promise<void> => {
            const patientContactId = patient?.IndividualResponse?.Contact_id;
            if (patientContactId) {
                const portalPatientOverview = await SharedItemClient.loadPortalAccountOverview(patientContactId);
                setPortalAccountOverview(portalPatientOverview);
            }
        };

        loadPortalAccountOverview();
    }, [patient]);

    useEffect(() => {
        if (onPortalCommunicationsCountRefresh && lastSharedItemIdWhenCountersWereRefreshed !== currentSharedItem?.SharedItemId) {
            onPortalCommunicationsCountRefresh?.();
            setLastSharedItemIdWhenCountersWereRefreshed(currentSharedItem?.SharedItemId);
        }
    }, [lastSharedItemIdWhenCountersWereRefreshed, onPortalCommunicationsCountRefresh, currentSharedItem?.SharedItemId]);

    const isSendButtonDisabled = !isFormValid || isSendInProgress;
    const isReplyMessageAction = actionType === SharedItemActionType.Reply;
    const isSharedItemArchived = !!currentSharedItem?.ArchivedByUserId && !!currentSharedItem?.DateArchived;
    const showReplyButton = !isSharedItemArchived && actionType === SharedItemActionType.View;
    const hasMessageFromProvider = currentSharedItem?.Messages?.some(p => !p.IsFromPatient);
    const showRevokeAllButton = currentSharedItem?.Status !== PortalSyncStatus.Revoked
        && hasMessageFromProvider;
    const patientMessages = currentSharedItem?.Messages?.filter(p => p.IsFromPatient) ?? [];
    const lastMessageFromPatient = patientMessages.reduce(
        (latest, current) =>
            new Date(current.DateCreated) > new Date(latest.DateCreated) ? current : latest,
        patientMessages[0]
    );
    const showMarkAsUnreadButton = !!lastMessageFromPatient
    && (!currentSharedItem?.DateReadByUser || new Date(currentSharedItem.DateReadByUser) > new Date(lastMessageFromPatient.DateCreated));
    const isSharedItemBelongToCurrentUser =
        !!currentSharedItem?.EmployeeContactId
        && sessionStore.getCurrentEmployee()?.ContactId === currentSharedItem.EmployeeContactId;

    if (!patient || !currentSharedItem || (props.showPatientBanner && !patientBannerModel))
        return null;

    const renderFooter = (): JSX.Element => (
        <div className={styles.bottomBar}>
            <div className={`${styles.buttons} ${isReplyMessageAction ? styles.newReplyMessage : ""}`}>
                {actionType === SharedItemActionType.View && currentSharedItem && (
                    <>
                        <div className={`${styles.buttonGroupContainer}`}>
                            {props.pagination && (
                                <div className={styles.paginator} data-cy="portal-communication-pagination">
                                    {props.pagination}
                                </div>
                            )}
                            <Button
                                category="primary"
                                onClick={async () => {
                                    const shouldArchive = !isSharedItemArchived;
                                    await SharedItemClient.updatePortalCommunicationsArchive([props.currentSharedItemId], shouldArchive);

                                    props.onArchivedRestore?.(props.currentSharedItemId);
                                }}
                                label={getText(isSharedItemArchived ? "RestoreLabel" : "ArchiveLabel")}
                                variant="brand"
                                disabled={!isSharedItemBelongToCurrentUser && !isArchiveCheckboxChecked}
                            />
                            {showRevokeAllButton && (
                                <Button
                                    category="primary"
                                    variant="error"
                                    label={getText("RevokeAllLabel")}
                                    onClick={() => setShowRevokeAllWarningPopup(true)}
                                />
                            )}
                            {showMarkAsUnreadButton && (
                                <Button
                                    category="secondary"
                                    variant="neutral"
                                    label={getText("MarkAsUnreadLabel")}
                                    onClick={() => handleMarkPortalCommunicationAsUnread()}
                                />
                            )}
                            {!isSharedItemBelongToCurrentUser && (
                                <div className={styles.checkboxArchivedRestoreContainer}>
                                    <CheckboxControl
                                        checked={isArchiveCheckboxChecked}
                                        onChange={() => setIsArchiveCheckboxChecked(p => !p)}
                                        label={getText(isSharedItemArchived ? "SharedItem_RestoreInstructionLabel" : "SharedInboxCheckboxLabel")}
                                        labelClassName={styles.checkboxLabel}
                                    />
                                </div>
                            )}
                        </div>
                        <div className={styles.leftActions}>
                            {props.showFlagForReview && (
                                <CheckboxControl
                                    label={getText("SharedItem_FlagForReviewLabel")}
                                    checked={currentSharedItem.IsFlaggedForReview}
                                    onChange={(isChecked: boolean) => handleFlagPortalCommunicationForReview(isChecked)}
                                    labelClassName={styles.checkboxLabel}
                                    dataCy="portal-communications-details-flag-for-review-checkbox"
                                />
                            )}
                            <div className={styles.leftButtons}>
                                {props.showCloseButton && (
                                    <Button
                                        category="secondary"
                                        onClick={() => props.onCloseCommunication?.()}
                                        label={getText("CloseLabel")}
                                        variant="neutral"
                                    />
                                )}
                                {showReplyButton && (
                                    <Button
                                        category="primary"
                                        variant="brand"
                                        label={getText("ReplyLabel")}
                                        onClick={() => {
                                            setActionType(SharedItemActionType.Reply);
                                        }}
                                    />
                                )}
                            </div>
                        </div>
                    </>
                )}
                {isReplyMessageAction && (
                    <div className={styles.buttonGroupContainer}>
                        <Button
                            category="secondary"
                            variant="neutral"
                            label={getText("CancelLabel")}
                            onClick={() => {
                                setActionType(SharedItemActionType.View);
                            }}
                        />
                        <Button
                            category="primary"
                            variant="brand"
                            label={getText("SendLabel")}
                            onClick={handleSendMessageClick}
                            disabled={isSendButtonDisabled}
                        />
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <div
            className={`${styles.communicationsMainContainer} ${props.usedDynamicHeight ? styles.usedDynamicHeight : ""}`}
            data-cy="portal-communication-view-container"
        >
            {showRevokeAllWarningPopup && (
                <PortalCommunicationRevokeAllPopup
                    onRevoke={() => revokeSharedItem()}
                    onCancel={() => setShowRevokeAllWarningPopup(false)}
                />
            )}
            {currentSharedItem && (
                <>
                    {currentSharedItem && (
                        <div className={`${styles.header} myle-page-header`}>
                            <div>
                                {getText("MessageDetailsLabel")}
                            </div>
                            <div className={styles.threadSubject}>
                                {currentSharedItem.Subject}
                            </div>
                        </div>
                    )}
                    <div className={styles.messagesViewContainer}>
                        <div className={styles.messagesList}>
                            <div className={`${props.usedDynamicHeight ? styles.usedDynamicHeight : styles.calculatedHeight}`}>
                                <PortalCommunicationMessagesList
                                    patient={patient}
                                    hideHeader
                                    sharedItemData={currentSharedItem}
                                    onPriorityChange={(sharedItemMessageId, selectedPriority) => { handlePatientMessagePriorityChange(sharedItemMessageId, selectedPriority); }}
                                    onCategoryChange={(categoryId: string) => handleSharedItemCategoryChange(categoryId)}
                                    onSharedItemMessageStatusToggleAsync={handleSharedItemMessageStatusToggleAsync}
                                    onSharedItemAttachmentStatusToggleAsync={syncOrRevokeSharedItemsAttachment}
                                />
                                {actionType === SharedItemActionType.Reply && currentSharedItem && (
                                    <PortalCommunicationMessageForm
                                        key={"PortalCommunicationMessageFormKey" + actionType}  // This is to force re-rendering of the form when switching between New and Reply
                                        componentId="CommunicationTab_NewMessageForm"
                                        sharedItem={currentSharedItem}
                                        currentPatient={Person.FromPatient(patient)}
                                        actionType={actionType}
                                        containerClass={`${styles.messageFormContainer} ${styles.replyForm}`}
                                        formContainerClass={styles.formContainer}
                                        currentPatientPortalOverview={portalAccountOverview}
                                        showHeader={actionType === SharedItemActionType.Reply}
                                        onNewPortalCommunicationMessageChange={newState => onSetFormState({
                                            ...formState,
                                            ...newState
                                        })}
                                    />
                                )}
                                {currentSharedItem && (
                                    <PortalCommunicationsMessagesNote
                                        sharedItem={currentSharedItem}
                                        onSharedItemUpdate={sharedItemNote => setCurrentSharedItem({ ...currentSharedItem, Notes: [sharedItemNote, ...currentSharedItem.Notes] })}
                                    />
                                )}
                            </div>
                        </div>
                        {props.showPatientBanner && (
                            <div className={`myle-form ${styles.patientVerticalBannerContainer}`} data-cy="portal-communication-patient-vertical-banner-container">
                                <PatientBannerVertical
                                    model={patientBannerModel}
                                    addPortalCommunicationSection
                                    portalCommunicationListUpdateKey={currentSharedItem.Messages.length}
                                />
                            </div>
                        )}
                    </div>
                    {renderFooter()}
                </>
            )}
        </div>
    );
}

export { PortalCommunicationsView };
